import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, KeyRound } from "lucide-react";
import { api, errMsg, fmtDate, roleStyle } from "../lib/api";
import { useRoles } from "../context/RolesContext";
import { PageHeader, Panel, inputCls } from "../components/ui-kit";
import { StatusBadge } from "../components/Badges";
import { ResetPasswordDialog } from "../components/ResetPasswordDialog";

const RolePicker = ({ u, onChange }) => (
  <div className="flex flex-wrap gap-1.5">
    {useRoles().roles.map((r) => {
      const k = r.key;
      const on = u.roles.includes(k);
      return (
        <button key={k} data-testid={`user-role-toggle-${u.id}-${k}`} onClick={() => onChange(on ? u.roles.filter((x) => x !== k) : [...u.roles, k])}
          style={on ? roleStyle(r.color) : undefined}
          className={`rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${on ? "" : "border-[#282E3D] text-slate-500 hover:text-slate-200"}`}>
          {r.label}
        </button>
      );
    })}
  </div>
);

const UserRow = ({ u, save, onReset }) => (
  <Panel data-testid={`user-row-${u.id}`} className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center">
    <div className="min-w-0 lg:w-64">
      <div className="flex items-center gap-2"><span className="font-semibold">{u.name}</span><StatusBadge status={u.status} testId={`user-status-${u.id}`} /></div>
      <div className="truncate text-xs text-slate-500">{u.email}</div>
      <div className="mt-1 font-mono text-[10px] text-slate-600">Joined {fmtDate(u.created_at)}</div>
    </div>
    <div className="flex-1">{u.is_admin ? <span className="text-xs text-orange-300">Owner · all roles</span> : <RolePicker u={u} onChange={(roles) => save(u, { roles })} />}</div>
    <div className="flex flex-wrap gap-2">
      <button data-testid={`admin-reset-password-button-${u.id}`} onClick={() => onReset(u)} className="inline-flex items-center gap-1 rounded-lg border border-[#282E3D] px-3 py-2 text-xs font-semibold text-slate-300 hover:border-[#FF6B00]/60 hover:text-white"><KeyRound className="h-3.5 w-3.5" />Reset password</button>
      {!u.is_admin && u.status !== "approved" && <button data-testid={`admin-approve-user-button-${u.id}`} onClick={() => save(u, { status: "approved" })} className="inline-flex items-center gap-1 rounded-lg bg-emerald-500/15 px-3 py-2 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/25"><Check className="h-3.5 w-3.5" />Approve</button>}
      {!u.is_admin && u.status !== "rejected" && <button data-testid={`admin-reject-user-button-${u.id}`} onClick={() => save(u, { status: "rejected" })} className="inline-flex items-center gap-1 rounded-lg bg-rose-500/10 px-3 py-2 text-xs font-semibold text-rose-300 hover:bg-rose-500/20"><X className="h-3.5 w-3.5" />{u.status === "approved" ? "Revoke" : "Reject"}</button>}
    </div>
  </Panel>
);

export default function Users() {
  const [users, setUsers] = useState([]);
  const [resetting, setResetting] = useState(null);
  const [q, setQ] = useState("");
  const [showRejected, setShowRejected] = useState(false);
  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);
  const save = async (u, body) => {
    try {
      await api.patch(`/users/${u.id}`, body);
      toast.success(body.status ? `${u.name} ${body.status}` : "Roles updated");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const term = q.trim().toLowerCase();
  const match = (u) => !term || u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term);
  const pending = users.filter((u) => u.status === "pending" && match(u));
  const rejectedCount = users.filter((u) => u.status === "rejected" && match(u)).length;
  const rest = users.filter((u) => u.status !== "pending" && match(u) && (showRejected || u.status !== "rejected"));
  return (
    <div>
      <PageHeader eyebrow="Team" title="Team & approvals" desc="Approve new registrations, assign roles and reset passwords. A person can hold multiple roles." />
      <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center">
        <input data-testid="users-search-input" placeholder="Search by name or email…" className={`${inputCls} sm:max-w-sm`} value={q} onChange={(e) => setQ(e.target.value)} />
        <button data-testid="users-show-rejected-toggle" onClick={() => setShowRejected(!showRejected)} className={`rounded-full border px-4 py-1.5 text-xs font-medium transition-colors ${showRejected ? "border-[#FF6B00] bg-[#FF6B00] text-white" : "border-[#282E3D] text-slate-400 hover:text-white"}`}>
          {showRejected ? "Hide" : "Show"} rejected ({rejectedCount})
        </button>
      </div>
      <section className="mb-10">
        <h2 className="mb-4 font-heading text-lg font-semibold">Awaiting approval <span className="ml-1 font-mono text-sm text-amber-300" data-testid="pending-count">{pending.length}</span></h2>
        {pending.length ? <div className="space-y-3">{pending.map((u) => <UserRow key={u.id} u={u} save={save} onReset={setResetting} />)}</div> : <p className="text-sm text-slate-500" data-testid="no-pending-users">No pending registrations.</p>}
      </section>
      <section>
        <h2 className="mb-4 font-heading text-lg font-semibold">Team members</h2>
        <div className="space-y-3">{rest.map((u) => <UserRow key={u.id} u={u} save={save} onReset={setResetting} />)}</div>
      </section>
      {resetting && <ResetPasswordDialog user={resetting} onClose={() => setResetting(null)} />}
    </div>
  );
}
